import React, { useState, useEffect } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { LOGO_URL, waUrl } from "../lib/constants";

// Hide on dedicated funnel/print pages
const HIDDEN_NAV_ROUTES = [
    "/live",
    "/watch",
    "/sleep",
    "/plan",
    "/session",
    "/investors",
    "/dapperdan",
    "/thank-you",
    "/brochure",
    "/brochure-corporate",
    "/brochure-gym",
    "/brochure-realestate",
    "/brochure-athletes",
    "/brochure-equine",
    "/brochure-pets",
    "/brochure-yacht",
    "/brochure-bvlgari",
    "/resume",
    "/cover-letter",
    "/application",
    "/cover-letter-portable",
    "/application-portable",
    "/cover-letter-wynn",
    "/application-wynn",
    "/application-wynn-stage",
    "/application-wynn-automation",
    "/application-tether",
    "/reel",
    "/sunrise-pitch",
];

const NAV_RESERVE_URL = "https://buy.stripe.com/aFa5kE6PG4q40YBbyZ5Vu02"; // $29.99 Stripe direct (pay-first) — redirects to /thank-you?sku=single
const NAV_WA_URL = waUrl(
    "Hi Aditya, I'd like to reserve a session at The Reset Sanctuary.",
);

// Pack fbp+fbc+ua into Stripe client_reference_id so the webhook can hash them into the Purchase CAPI event (max EMQ).
const buildStripeUrlWithCrid = (baseUrl) => {
    if (typeof document === "undefined") return baseUrl;
    try {
        const getCookie = (n) => {
            const v = document.cookie.match("(^|;)\\s*" + n + "\\s*=\\s*([^;]+)");
            return v ? v.pop() : null;
        };
        const fbp = getCookie("_fbp");
        const fbc = getCookie("_fbc");
        const ua = (typeof navigator !== "undefined" ? navigator.userAgent : "").slice(0, 80);
        const payload = { fbp, fbc, ua, t: Date.now() };
        const json = JSON.stringify(payload);
        const crid = btoa(unescape(encodeURIComponent(json)))
            .replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
        return `${baseUrl}?client_reference_id=${crid}`;
    } catch (err) { return baseUrl; }
};

// Fire InitiateCheckout pixel + CAPI mirror on nav-Reserve click, then open Stripe in new tab (parity with /live hero CTA)
const handleNavReserveClick = (e) => {
    if (e && e.preventDefault) e.preventDefault();
    const eventId = `nav-ic-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    if (typeof window !== "undefined" && typeof window.fbq === "function") {
        try {
            window.fbq("track", "InitiateCheckout", {
                content_name: "Sleep & Anxiety Broadcast",
                value: 29.99,
                currency: "USD",
                eventID: eventId,
            });
        } catch (err) {}
    }
    if (typeof window !== "undefined" && process.env.REACT_APP_BACKEND_URL) {
        const getCookie = (n) => {
            const v = document.cookie.match("(^|;)\\s*" + n + "\\s*=\\s*([^;]+)");
            return v ? v.pop() : null;
        };
        try {
            fetch(`${process.env.REACT_APP_BACKEND_URL}/api/meta/capi`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    event_name: "InitiateCheckout",
                    event_source_url: window.location.href,
                    event_id: eventId,
                    fbp: getCookie("_fbp"),
                    fbc: getCookie("_fbc"),
                    client_user_agent: navigator.userAgent,
                    value: 29.99,
                    currency: "USD",
                    content_name: "Sleep & Anxiety Broadcast",
                }),
                keepalive: true,
            }).catch(() => {});
        } catch (err) {}
    }
    // Open Stripe with client_reference_id wrap
    if (typeof window !== "undefined") {
        window.open(buildStripeUrlWithCrid(NAV_RESERVE_URL), "_blank", "noopener,noreferrer");
    }
};

const NAV_ITEMS = [
    { to: "/", label: "Home" },
    { to: "/about", label: "About" },
    { to: "/science", label: "The Science Behind It" },
    { to: "/live", label: "Live" },
    { to: "/book", label: "Book" },
    { to: "/contact", label: "Contact" },
];

export const Navigation = () => {
    const [open, setOpen] = useState(false);
    const [scrolled, setScrolled] = useState(false);
    const location = useLocation();

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 30);
        window.addEventListener("scroll", onScroll);
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    useEffect(() => {
        setOpen(false);
    }, [location.pathname]);

    useEffect(() => {
        document.body.style.overflow = open ? "hidden" : "";
        return () => {
            document.body.style.overflow = "";
        };
    }, [open]);

    if (HIDDEN_NAV_ROUTES.includes(location.pathname)) return null;

    return (
        <>
            <header
                data-testid="main-navigation"
                style={{
                    position: "fixed",
                    top: 0,
                    left: 0,
                    right: 0,
                    zIndex: 50,
                    backgroundColor: scrolled
                        ? "rgba(10,10,10,0.85)"
                        : "rgba(10,10,10,0.4)",
                    backdropFilter: "blur(14px)",
                    WebkitBackdropFilter: "blur(14px)",
                    borderBottom: scrolled
                        ? "1px solid rgba(201,162,39,0.12)"
                        : "1px solid transparent",
                    transition: "all 0.35s ease",
                }}
            >
                <nav
                    style={{
                        maxWidth: 1400,
                        margin: "0 auto",
                        padding: "18px 32px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        gap: 24,
                    }}
                >
                    <Link
                        to="/"
                        data-testid="nav-logo-link"
                        style={{ display: "flex", alignItems: "center", gap: 12 }}
                    >
                        <img
                            src={LOGO_URL}
                            alt="The Reset Sanctuary"
                            style={{ height: 52, width: "auto", display: "block" }}
                            data-testid="nav-logo-img"
                        />
                    </Link>

                    {/* Desktop nav */}
                    <ul
                        className="rs-desktop-nav"
                        style={{
                            listStyle: "none",
                            margin: 0,
                            padding: 0,
                            gap: 32,
                            alignItems: "center",
                            flexWrap: "nowrap",
                        }}
                    >
                        {NAV_ITEMS.map((item) => (
                            <li key={item.to}>
                                <NavLink
                                    to={item.to}
                                    end={item.to === "/"}
                                    data-testid={`nav-link-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
                                    className={({ isActive }) =>
                                        `nav-link ${isActive ? "active" : ""}`
                                    }
                                    style={({ isActive }) => ({
                                        color: isActive ? "#c9a227" : "#ffffff",
                                        textDecoration: "none",
                                        fontSize: 11,
                                        fontWeight: 500,
                                        letterSpacing: "0.22em",
                                        textTransform: "uppercase",
                                        whiteSpace: "nowrap",
                                    })}
                                >
                                    {item.label}
                                </NavLink>
                            </li>
                        ))}
                    </ul>

                    <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                        <a
                            href={NAV_RESERVE_URL}
                            onClick={handleNavReserveClick}
                            className="btn-gold-outline rs-desktop-only"
                            data-testid="nav-reserve-btn"
                        >
                            Reserve
                        </a>
                        <button
                            onClick={() => setOpen(true)}
                            aria-label="Open menu"
                            data-testid="mobile-menu-open-btn"
                            className="rs-mobile-only"
                            style={{
                                background: "transparent",
                                border: "1px solid rgba(201,162,39,0.4)",
                                color: "#c9a227",
                                padding: 10,
                                cursor: "pointer",
                                alignItems: "center",
                                justifyContent: "center",
                            }}
                        >
                            <Menu size={20} />
                        </button>
                    </div>
                </nav>
            </header>

            {/* Mobile menu overlay */}
            {open && (
                <div
                    className="mobile-menu-overlay"
                    data-testid="mobile-menu-overlay"
                >
                    <button
                        onClick={() => setOpen(false)}
                        aria-label="Close menu"
                        data-testid="mobile-menu-close-btn"
                        style={{
                            position: "absolute",
                            top: 24,
                            right: 24,
                            background: "transparent",
                            border: "1px solid rgba(201,162,39,0.4)",
                            color: "#c9a227",
                            padding: 10,
                            cursor: "pointer",
                        }}
                    >
                        <X size={20} />
                    </button>
                    <img
                        src={LOGO_URL}
                        alt="The Reset Sanctuary"
                        style={{ height: 80, marginBottom: 16 }}
                    />
                    {NAV_ITEMS.map((item) => (
                        <NavLink
                            key={item.to}
                            to={item.to}
                            end={item.to === "/"}
                            data-testid={`mobile-nav-link-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
                            style={({ isActive }) => ({
                                color: isActive ? "#c9a227" : "#ffffff",
                                fontSize: 28,
                                fontFamily: "Playfair Display, serif",
                                textDecoration: "none",
                                letterSpacing: "0.04em",
                            })}
                        >
                            {item.label}
                        </NavLink>
                    ))}
                    <a
                        href={NAV_RESERVE_URL}
                        onClick={(e) => { setOpen(false); handleNavReserveClick(e); }}
                        className="btn-gold-solid"
                        data-testid="mobile-nav-reserve-btn"
                        style={{ marginTop: 20 }}
                    >
                        Reserve — $29.99
                    </a>
                    <a
                        href={NAV_WA_URL}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={() => setOpen(false)}
                        className="btn-gold-outline"
                        data-testid="mobile-nav-wa-btn"
                        style={{ marginTop: 12 }}
                    >
                        Questions? WhatsApp Us
                    </a>
                </div>
            )}
        </>
    );
};

export default Navigation;
